import { CandidateLayout } from '@/components/layout/CandidateLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Building2,
  MapPin,
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  Briefcase,
} from 'lucide-react';

interface Application {
  id: string;
  company: string;
  position: string;
  location: string;
  appliedDate: string;
  status: 'pending' | 'shortlisted' | 'interviewed' | 'rejected' | 'hired';
  interviewDate?: string;
  interviewTime?: string;
}

const applications: Application[] = [
  {
    id: '1',
    company: 'TechCorp Inc.',
    position: 'Senior Frontend Developer',
    location: 'Bangalore, India',
    appliedDate: '2024-01-15',
    status: 'shortlisted',
    interviewDate: '2024-01-28',
    interviewTime: '10:00 AM',
  },
  {
    id: '2',
    company: 'StartupXYZ',
    position: 'Full Stack Developer',
    location: 'Remote',
    appliedDate: '2024-01-10',
    status: 'interviewed',
  },
  {
    id: '3',
    company: 'MegaCorp',
    position: 'React Developer',
    location: 'Mumbai, India',
    appliedDate: '2024-01-05',
    status: 'hired',
  },
  {
    id: '4',
    company: 'GlobalTech',
    position: 'Frontend Engineer',
    location: 'Delhi, India',
    appliedDate: '2024-01-02',
    status: 'rejected',
  },
  {
    id: '5',
    company: 'InnovateTech',
    position: 'UI Developer',
    location: 'Pune, India',
    appliedDate: '2024-01-20',
    status: 'pending',
  },
];

const CandidateApplications = () => {
  const getStatusBadge = (status: Application['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'shortlisted':
        return <Badge variant="default">Shortlisted</Badge>;
      case 'interviewed':
        return <Badge className="bg-accent text-accent-foreground">Interviewed</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      case 'hired':
        return <Badge className="bg-success text-success-foreground">Hired</Badge>;
    }
  };

  const getStatusIcon = (status: Application['status']) => {
    switch (status) {
      case 'shortlisted':
        return <CheckCircle2 className="h-5 w-5 text-primary" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'hired':
        return <Briefcase className="h-5 w-5 text-success" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const filterApplications = (status?: Application['status'] | 'all') => {
    if (!status || status === 'all') return applications;
    return applications.filter((app) => app.status === status);
  };

  const ApplicationCard = ({ app }: { app: Application }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Building2 className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
              <h3 className="font-semibold truncate">{app.position}</h3>
              {getStatusBadge(app.status)}
            </div>
            <p className="text-sm font-medium text-muted-foreground mb-2">
              {app.company}
            </p>
            <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {app.location}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Applied: {new Date(app.appliedDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
            {app.interviewDate && (
              <div className="mt-3 flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-accent" />
                <span className="text-accent font-medium">
                  Interview: {new Date(app.interviewDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })} at {app.interviewTime}
                </span>
              </div>
            )}
          </div>
          <div className="hidden sm:block">{getStatusIcon(app.status)}</div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <CandidateLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">My Applications</h1>
          <p className="text-muted-foreground">
            Track all your job applications and their status
          </p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="all">
          <TabsList className="w-full flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="all" className="flex-1 min-w-[80px]">
              All ({applications.length})
            </TabsTrigger>
            <TabsTrigger value="shortlisted" className="flex-1 min-w-[80px]">
              Shortlisted ({filterApplications('shortlisted').length})
            </TabsTrigger>
            <TabsTrigger value="rejected" className="flex-1 min-w-[80px]">
              Rejected ({filterApplications('rejected').length})
            </TabsTrigger>
            <TabsTrigger value="hired" className="flex-1 min-w-[80px]">
              Hired ({filterApplications('hired').length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-4 space-y-3">
            {applications.map((app) => (
              <ApplicationCard key={app.id} app={app} />
            ))}
          </TabsContent>

          <TabsContent value="shortlisted" className="mt-4 space-y-3">
            {filterApplications('shortlisted').length > 0 ? (
              filterApplications('shortlisted').map((app) => (
                <ApplicationCard key={app.id} app={app} />
              ))
            ) : (
              <EmptyState status="shortlisted" />
            )}
          </TabsContent>

          <TabsContent value="rejected" className="mt-4 space-y-3">
            {filterApplications('rejected').length > 0 ? (
              filterApplications('rejected').map((app) => (
                <ApplicationCard key={app.id} app={app} />
              ))
            ) : (
              <EmptyState status="rejected" />
            )}
          </TabsContent>

          <TabsContent value="hired" className="mt-4 space-y-3">
            {filterApplications('hired').length > 0 ? (
              filterApplications('hired').map((app) => (
                <ApplicationCard key={app.id} app={app} />
              ))
            ) : (
              <EmptyState status="hired" />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </CandidateLayout>
  );
};

const EmptyState = ({ status }: { status: string }) => (
  <Card>
    <CardContent className="flex flex-col items-center justify-center py-12">
      <Briefcase className="h-12 w-12 text-muted-foreground/50 mb-4" />
      <p className="text-lg font-medium text-muted-foreground">
        No {status} applications
      </p>
      <p className="text-sm text-muted-foreground">
        Applications with this status will appear here
      </p>
    </CardContent>
  </Card>
);

export default CandidateApplications;
